import { format, parse, tz } from '@sequencehq/utils/dates'
import { match } from 'ts-pattern'

export type UpgradeShortcut = {
  type: 'upgrade'
  startDate: Date
}

export type RenewShortcut = {
  type: 'renew'
  phaseIndex: number
}

export type CubeShortcut = UpgradeShortcut | RenewShortcut

/**
 * The format of the shortcut when stored in LS
 */
type LSUpgradeShortcut = {
  type: 'upgrade'
  startDate: string
}
type LSRenewShortcut = {
  type: 'renew'
  phaseIndex: number
}

type LSCubeShortcut = LSUpgradeShortcut | LSRenewShortcut
const LS_DATE_FORMAT = 'yyyy-MM-dd'

const parseShortcut = (shortcut: string): CubeShortcut => {
  const parsedShortcut = JSON.parse(shortcut) as LSCubeShortcut
  return match(parsedShortcut)
    .with(
      {
        type: 'renew'
      },
      sc => sc
    )
    .with({ type: 'upgrade' }, sc => {
      return {
        ...sc,
        startDate: parse(sc.startDate, LS_DATE_FORMAT, new Date())
      }
    })
    .exhaustive()
}

/**
 * A one shot retrieval of a cube shortcut item - removes from local
 * storage after retrieval.
 * @param shortcutUuid
 * @returns
 */
const get = (shortcutUuid: string): CubeShortcut | null => {
  const shortcut = localStorage.getItem(`cube:shortcut:${shortcutUuid}`)

  if (!shortcut) {
    return null
  }

  try {
    return parseShortcut(shortcut)
  } catch (e) {
    console.error('Failed to parse shortcut', e)
    return null
  } finally {
    localStorage.removeItem(`cube:shortcut:${shortcutUuid}`)
  }
}

const stringifyShortcut = (shortcut: CubeShortcut): string => {
  const formattedShortcut: LSCubeShortcut = match(shortcut)
    .with(
      {
        type: 'renew'
      },
      sc => sc
    )
    .with({ type: 'upgrade' }, sc => ({
      ...sc,
      startDate: format(sc.startDate, LS_DATE_FORMAT)
    }))
    .exhaustive()
  return JSON.stringify(formattedShortcut)
}

/**
 * Sets a cube shortcut in local storage for use within cube, the uuid should
 * be passed as a query param to Cube.
 * @param shortcut
 * @returns
 */
const set = (shortcut: CubeShortcut): string => {
  const uuid = crypto.randomUUID()

  localStorage.setItem(`cube:shortcut:${uuid}`, stringifyShortcut(shortcut))
  return uuid
}

export const cubeUpdateShortcuts = {
  get,
  set,
  queryParam: 'shortcutUuid'
}
